import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  couponSchema,
  CouponFormData,
  StatusEnum,
  CouponData,
  CouponTypeEnum,
} from "../schema/coupon-schema";
import { useCreateCoupon } from "../hooks/use-create-coupon";
import { useUpdateCoupon } from "../hooks/use-update-coupon";
import { useUpdateCouponStatus } from "../hooks/use-update-coupon-status";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useState } from "react";
import { useActiveBundles } from "../hooks/use-active-bundles";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
// Custom picker instead of <Input type="datetime-local">: the native picker's
// icon clicks get eaten by the vaul drawer's drag handlers so it never opens,
// and the team wanted an explicit Done step after picking date + time.
import CouponDateTimeField from "./coupon-date-time-field";
import { Bundle } from "@/pages/bundles/types/bundle";
// Live list from GET /region/active — same source the server validates
// against, so the dropdown can never offer a currency the API rejects.
import useSupportedCurrencies from "@/features/regions/hooks/use-supported-currencies";
// Searchable picker shared with the create-pack flow; used here in
// pick-and-add mode (no `value`, selections live in the chips below).
import { BundleCombobox } from "@/features/users/ui/bundle-combobox";

// Sentinel for the Associated Bundles region filter: show every region.
const ALL_BUNDLE_REGIONS = "all";

// Detail line for the bundle picker: price + region, because this picker can
// span all regions (unlike the create-pack cascade). Module-scoped — an
// inline arrow would get a new identity each render and defeat the
// combobox's search-haystack memo (this form re-renders on every keystroke).
const getCouponBundleDetail = (bundle: Bundle) =>
  [
    [bundle.currency, bundle.amount].filter(Boolean).join(" "),
    bundle.region?.toUpperCase(),
  ]
    .filter(Boolean)
    .join(" · ");

type AddEditCouponProps = {
  editMode?: boolean;
  duplicateMode?: boolean;
  /**
   * Show the coupon without letting it be changed. Fields are disabled and the
   * mutating CTAs are replaced by Close. Used when the viewer has View but not
   * Edit on Coupons — the drawer still opens, it just cannot save.
   */
  readOnly?: boolean;
  setShowCouponDrawer?: (value: boolean) => void;
  onClose?: () => void;
  initialData?: CouponData;
  // Provided by the page: sanitizes the coupon being edited and reopens this
  // drawer in duplicate mode (the page remounts the form via its key).
  onDuplicate?: () => void;
};

export default function AddEditCouponForm({
  editMode,
  duplicateMode,
  readOnly = false,
  setShowCouponDrawer,
  onClose,
  initialData,
  onDuplicate,
}: AddEditCouponProps) {
  const form = useForm<CouponFormData>({
    resolver: zodResolver(couponSchema),
    defaultValues: initialData
      ? {
          ...initialData,
          bundle_ids: initialData.bundles?.map((bundle) => bundle.id) || [],
        }
      : {
          first_time_user_only: false,
          type: CouponTypeEnum.B2B,
          status: StatusEnum.ACTIVE,
          currency: "INR",
        },
  });

  const createCouponMutation = useCreateCoupon();
  const updateCouponMutation = useUpdateCoupon();
  const updateStatusMutation = useUpdateCouponStatus();
  // staleTime 0: revalidate the bundle list on every drawer open so a bundle
  // created/retired minutes ago is reflected (cache still renders instantly).
  const { data: activeBundlesData } = useActiveBundles({ staleTime: 0 });
  const { currencies } = useSupportedCurrencies();

  // Region filter for the Associated Bundles picker (create/edit/duplicate all
  // render this same form). Holds a Region.id (or the "all" sentinel) and
  // scopes the DROPDOWN list server-side via /bundle/active?region_id= — the
  // Bundle→Region relation, not the legacy Bundle.region string (whose values
  // drifted from Region.code: 'uae' vs 'ae'). It only narrows the options;
  // the coupon payload stays bundle_ids on POST /coupon (create & duplicate)
  // and PATCH /coupon/:id (edit). Defaults to "all regions" so edit/duplicate
  // of an existing coupon — which may already hold bundles from several
  // regions — opens exactly as before.
  const [bundleRegionId, setBundleRegionId] = useState(ALL_BUNDLE_REGIONS);
  const { data: regions } = useActiveRegions();
  // Dropdown OPTIONS only — the full unscoped list above keeps resolving the
  // selected chips and the fixed-discount currency check, so bundles already
  // attached from other regions (edit/duplicate) stay visible and removable.
  const { data: regionBundlesData, isLoading: isRegionBundlesLoading } =
    useActiveBundles({
      staleTime: 0,
      regionId:
        bundleRegionId === ALL_BUNDLE_REGIONS ? undefined : bundleRegionId,
    });

  const [isPercentageDiscount, setIsPercentageDiscount] = useState(
    Boolean(initialData?.discount_percentage)
  );

  // Drives the end date's minDate so the end calendar can't offer a day before
  // the chosen start.
  const startDate = form.watch("start_date");

  const onSubmit = async (data: CouponFormData) => {
    try {
      const formData = {
        ...data,
        discount_percentage: isPercentageDiscount
          ? data.discount_percentage
          : null,
        discount_amount: !isPercentageDiscount ? data.discount_amount : null,
        bundle_ids: data.bundle_ids || [],
        // description: data.description || undefined
      };

      // A fixed discount reaching a selected bundle's price would make it
      // free (Razorpay rejects zero-amount orders) or negative — block before
      // hitting the server, naming the bundle. Mirrors the server check:
      // same-currency bundles only (the picker is cross-region, and an INR
      // discount is not comparable to an AED price), and <= not <.
      if (!isPercentageDiscount && formData.discount_amount) {
        const selectedBundles = (activeBundlesData?.bundles || []).filter(
          (bundle: Bundle) => formData.bundle_ids.includes(bundle.id)
        );
        const offendingBundle = selectedBundles.find(
          (bundle: Bundle) =>
            (!formData.currency ||
              !bundle.currency ||
              bundle.currency.toUpperCase() ===
                formData.currency.toUpperCase()) &&
            bundle.amount <= formData.discount_amount!
        );
        if (offendingBundle) {
          form.setError("discount_amount", {
            message: `Discount amount ${formData.discount_amount} must be less than bundle ${offendingBundle.name} amount (${offendingBundle.amount})`,
          });
          return;
        }
      }

      if (editMode && initialData?.id) {
        await updateCouponMutation.mutateAsync({
          id: initialData.id,
          data: formData,
        });
      } else {
        await createCouponMutation.mutateAsync(formData);
      }
      setShowCouponDrawer?.(false);
    } catch (error) {
      // No toast here on purpose: the mutation hook's onError already shows
      // the server's specific message via getApiErrorMessage — a second
      // generic toast would stack on top and hide it. This log is dev-only.
      console.error("Error submitting form:", error);
    }
  };

  const handleStatusChange = async () => {
    if (editMode && initialData?.id) {
      const newStatus =
        initialData.status === StatusEnum.ACTIVE
          ? StatusEnum.INACTIVE
          : StatusEnum.ACTIVE;
      try {
        await updateStatusMutation.mutateAsync({
          id: initialData.id,
          status: newStatus,
        });
        setShowCouponDrawer?.(false);
      } catch (error) {
        // The status hook's onError toast shows the server's message;
        // keep the drawer open so the state stays visible.
        console.error("Error updating coupon status:", error);
      }
    }
  };

  const handleDiscountTypeChange = (value: "percentage" | "fixed") => {
    const isPercentage = value === "percentage";
    setIsPercentageDiscount(isPercentage);
    if (isPercentage) {
      form.setValue("discount_amount", null);
    } else {
      form.setValue("discount_percentage", null);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-10 overflow-x-hidden min-w-0">
        <div className="bg-sidebar rounded h-16 flex items-center px-8">
          <h4 className="text-xl text-white">
            {readOnly
              ? ""
              : editMode
                ? "Edit "
                : duplicateMode
                  ? "Duplicate "
                  : "Add "}
            Coupon
          </h4>
        </div>

        <div className="px-6 space-y-6">
          <fieldset disabled={readOnly} className="space-y-6 min-w-0">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4>Basic Information</h4>
            <div className="grid grid-cols-2 gap-4 min-w-0">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Coupon Name"
                        {...field}
                        className="shadow-none drop-shadow-none"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger className="shadow-none drop-shadow-none">
                        <SelectValue placeholder="Select Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={CouponTypeEnum.INTERNAL}>
                          INTERNAL
                        </SelectItem>
                        <SelectItem value={CouponTypeEnum.CAMPAIGN}>
                          CAMPAIGN
                        </SelectItem>
                        <SelectItem value={CouponTypeEnum.COMMUNITY}>
                          COMMUNITY
                        </SelectItem>
                        <SelectItem value={CouponTypeEnum.B2B}>B2B</SelectItem>
                        <SelectItem value={CouponTypeEnum.B2B2C}>
                          B2B2C
                        </SelectItem>
                        <SelectItem value={CouponTypeEnum.AFFILIATE}>
                          AFFILIATE
                        </SelectItem>
                        <SelectItem value={CouponTypeEnum.SERVICE_ADJUSTMENT}>
                          SERVICE ADJUSTMENT
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Discount Settings */}
          <div className="space-y-4">
            <h4>Discount Settings</h4>
            <div className="grid grid-cols-2 gap-4 min-w-0">
              {/* Discount Type Selection */}
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  Discount Type <span className="text-red-500">*</span>
                </FormLabel>
                <Select
                  onValueChange={handleDiscountTypeChange}
                  defaultValue={isPercentageDiscount ? "percentage" : "fixed"}
                  value={isPercentageDiscount ? "percentage" : "fixed"}
                >
                  <SelectTrigger className="shadow-none drop-shadow-none">
                    <SelectValue placeholder="Select discount type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>

              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value: string) => field.onChange(value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          {/* Children override the item label so the trigger
                              shows the short code (AED), not the full name */}
                          <SelectValue placeholder="Select currency">
                            {field.value}
                          </SelectValue>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {currencies.map((currency) => (
                          <SelectItem
                            key={currency.value}
                            value={currency.value}
                          >
                            {currency.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={
                  isPercentageDiscount
                    ? "discount_percentage"
                    : "discount_amount"
                }
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {isPercentageDiscount
                        ? "Discount Percentage"
                        : "Discount Amount"}{" "}
                      <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        className="shadow-none drop-shadow-none"
                        placeholder={
                          isPercentageDiscount
                            ? "Enter percentage (max 100)"
                            : "Enter amount"
                        }
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(
                            value === "" ? null : parseFloat(value)
                          );
                        }}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="discount_min"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Minimum Amount
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter minimum amount"
                        className="shadow-none drop-shadow-none"
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(
                            value === "" ? null : parseFloat(value)
                          );
                        }}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="discount_cap"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Maximum Discount Cap
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        className="shadow-none drop-shadow-none"
                        placeholder="Enter maximum discount cap (optional)"
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(
                            value === "" ? null : parseFloat(value)
                          );
                        }}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                    <p className="text-xs text-gray-500 mt-1">
                      Limits the maximum discount amount for percentage-based coupons or caps fixed discounts
                    </p>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="usage_limit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Usage Limit
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Leave blank for unlimited uses"
                        className="shadow-none drop-shadow-none"
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value === "" ? null : parseInt(value, 10));
                        }}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum number of times this coupon can be redeemed. Leave blank for unlimited.
                    </p>
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Date Range */}
          <div className="space-y-4">
            <h4>Validity Period</h4>
            <div className="grid grid-cols-2 gap-4 min-w-0">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Start Date <span className="text-red-500">*</span>
                    </FormLabel>
                    <CouponDateTimeField
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Pick start date & time"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      End Date
                    </FormLabel>
                    <CouponDateTimeField
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Pick end date & time (optional)"
                      minDate={startDate ? new Date(startDate) : undefined}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Bundle Selection */}
          <div className="space-y-4">
            <h4>Associated Bundles</h4>

            <FormField
              control={form.control}
              name="bundle_ids"
              render={({ field }) => {
                const bundles = activeBundlesData?.bundles || [];

                // Dropdown OPTIONS: server-scoped by region_id (see the query
                // above). The chips below keep resolving against the FULL
                // list, so bundles already attached from other regions
                // (edit/duplicate) stay visible and removable.
                const regionBundles: Bundle[] =
                  regionBundlesData?.bundles || [];

                // Distinct offerings can share a display name — append price/region
                // so CT can tell them apart in the dropdown and selected chips.
                // Counted over the FULL list so a name that repeats across
                // regions stays disambiguated even when the filter hides its twin.
                const nameCounts = bundles.reduce(
                  (acc: Record<string, number>, b: Bundle) => {
                    acc[b.name] = (acc[b.name] || 0) + 1;
                    return acc;
                  },
                  {}
                );
                const bundleLabel = (b: Bundle) =>
                  nameCounts[b.name] > 1
                    ? `${b.name} — ${b.currency} ${b.amount}${
                        b.region ? ` (${b.region.toUpperCase()})` : ""
                      }`
                    : b.name;

                return (
                  <FormItem>
                    <FormControl>
                      <div className="space-y-2">
                        <div className="flex gap-2 min-w-0">
                          {/* Region narrows the bundle dropdown; it is not a
                              form field and is never submitted. */}
                          <Select
                            value={bundleRegionId}
                            onValueChange={setBundleRegionId}
                          >
                            <SelectTrigger className="w-[180px] flex-shrink-0 shadow-none drop-shadow-none">
                              <SelectValue placeholder="Region" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={ALL_BUNDLE_REGIONS}>
                                All regions
                              </SelectItem>
                              {(regions ?? []).map((region) => (
                                <SelectItem key={region.id} value={region.id}>
                                  {region.display_name} (
                                  {region.code.toUpperCase()})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {/* Pick-and-add: no `value`, so the trigger always
                              shows the placeholder and selections live in the
                              chips below. Detail line overridden with
                              price + region because this picker can span all
                              regions (unlike the create-pack cascade). */}
                          <BundleCombobox
                            bundles={regionBundles}
                            onSelect={(bundleId) => {
                              const currentValues = field.value || [];
                              if (!currentValues.includes(bundleId)) {
                                field.onChange([...currentValues, bundleId]);
                              }
                            }}
                            // Select All = every bundle currently shown in the
                            // dropdown (region filter + search), MERGED into
                            // the existing selection. Replacing would silently
                            // drop already-attached bundles from other regions
                            // on edit.
                            onSelectAll={(visibleBundleIds) => {
                              field.onChange([
                                ...new Set([
                                  ...(field.value || []),
                                  ...visibleBundleIds,
                                ]),
                              ]);
                            }}
                            getDetail={getCouponBundleDetail}
                            placeholder={
                              isRegionBundlesLoading
                                ? "Loading bundles..."
                                : "Choose bundles"
                            }
                            emptyMessage={
                              isRegionBundlesLoading
                                ? "Loading bundles..."
                                : "No bundles in this region"
                            }
                            triggerClassName="flex-1 min-w-0 shadow-none drop-shadow-none"
                          />
                        </div>

                        {field.value && field.value.length > 0 && (
                          <>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {field.value.map((selectedId) => {
                                const bundle = bundles.find(
                                  (b: Bundle) => b.id === selectedId
                                );
                                return (
                                  bundle && (
                                    <div
                                      key={selectedId}
                                      className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full"
                                    >
                                      <span className="text-sm">
                                        {bundleLabel(bundle)}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newValues =
                                            field.value?.filter(
                                              (id) => id !== selectedId
                                            ) || [];
                                          field.onChange(newValues);
                                        }}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  )
                                );
                              })}
                            </div>
                            <div className="flex justify-end mt-1">
                              <button
                                type="button"
                                onClick={() => field.onChange([])}
                                className="text-xs text-red-500 hover:text-red-700 underline underline-offset-2"
                              >
                                Clear bundles
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>

          {/* Coupon internal description */}
          <div className="space-y-4">
            <h4>Description</h4>
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Internal description"
                      className="shadow-none drop-shadow-none"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value === "" ? null : value); // Convert empty string to null
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Settings */}
          <div className="space-y-4">
            <h4>Settings</h4>
            <FormField
              control={form.control}
              name="first_time_user_only"
              render={({ field }) => (
                <FormItem className="flex items-center gap-x-2">
                  <FormControl className="">
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="pb-1.5">First Time User Only</FormLabel>
                </FormItem>
              )}
            />
          </div>

          </fieldset>

          {/* CTAs — outside the fieldset so Close stays clickable */}
          <div className="flex justify-end gap-4 pt-24">
            {readOnly ? (
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
            {/* Duplicate only for a saved coupon being edited — a create or an
                unsaved duplicate has nothing to copy from */}
            {editMode && initialData?.id && onDuplicate && (
              <Button
                type="button"
                onClick={onDuplicate}
                className="bg-orange-100 text-orange-700 border border-orange-300 hover:bg-orange-200"
              >
                Duplicate
              </Button>
            )}
            {editMode && (
              <>
                {initialData?.status === "ACTIVE" ? (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleStatusChange}
                    disabled={updateStatusMutation.isPending}
                  >
                    Inactive
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleStatusChange}
                    disabled={updateStatusMutation.isPending}
                    className="bg-green-600 hover:bg-green-700 px-8"
                  >
                    Active
                  </Button>
                )}
              </>
            )}
            <Button
              type="submit"
              disabled={
                createCouponMutation.isPending || updateCouponMutation.isPending
              }
              className="bg-sidebar px-8 hover:bg-sidebar"
            >
              {editMode ? "Update" : "Create"}
            </Button>
              </>
            )}
          </div>
        </div>
      </form>
    </Form>
  );
}
